import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';

export default function PalpationDialog({ open, onOpenChange, pregnancy, onSave }) {
  const calculateDaysSinceConception = () => {
    if (!pregnancy?.conceptionDate) return 0;
    const conception = new Date(pregnancy.conceptionDate);
    const today = new Date();
    return Math.floor((today - conception) / (1000 * 60 * 60 * 24));
  };

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    daysSinceConception: calculateDaysSinceConception(),
    fetusDetected: true,
    heartbeat: null,
    fetusSize: 'normal',
    position: 'normal',
    veterinarian: '',
    notes: ''
  });

  const handleSubmit = () => {
    if (!formData.veterinarian) {
      toast.error('Debe indicar el nombre del veterinario');
      return;
    }

    onSave(formData);
    onOpenChange(false);
    
    // Reset form
    setFormData({
      date: new Date().toISOString().split('T')[0],
      daysSinceConception: calculateDaysSinceConception(),
      fetusDetected: true,
      heartbeat: null,
      fetusSize: 'normal',
      position: 'normal',
      veterinarian: '',
      notes: ''
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Registrar Palpación</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Fecha y Días */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Fecha de Palpación</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label>Días desde Concepción</Label>
              <Input
                type="number"
                value={formData.daysSinceConception}
                onChange={(e) => setFormData(prev => ({ ...prev, daysSinceConception: parseInt(e.target.value) }))}
                readOnly
                className="bg-muted"
              />
            </div>
          </div>

          {/* Feto Detectado */}
          <div className="space-y-2">
            <Label>¿Se detectó el feto?</Label>
            <RadioGroup 
              value={formData.fetusDetected.toString()} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, fetusDetected: value === 'true' }))}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="true" id="fetus-yes" />
                <Label htmlFor="fetus-yes" className="font-normal cursor-pointer">
                  Sí, feto detectado
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="false" id="fetus-no" />
                <Label htmlFor="fetus-no" className="font-normal cursor-pointer">
                  No se detectó feto
                </Label>
              </div>
            </RadioGroup>
          </div>

          {formData.fetusDetected && (
            <>
              {/* Latido Cardíaco */}
              <div className="space-y-2">
                <Label>Latido Cardíaco</Label>
                <RadioGroup 
                  value={formData.heartbeat === null ? 'null' : formData.heartbeat.toString()}
                  onValueChange={(value) => setFormData(prev => ({ 
                    ...prev, 
                    heartbeat: value === 'null' ? null : value === 'true' 
                  }))}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="heartbeat-yes" />
                    <Label htmlFor="heartbeat-yes" className="font-normal cursor-pointer">
                      Latido detectado
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="heartbeat-no" />
                    <Label htmlFor="heartbeat-no" className="font-normal cursor-pointer">
                      No se detectó latido
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="null" id="heartbeat-na" />
                    <Label htmlFor="heartbeat-na" className="font-normal cursor-pointer">
                      No evaluado
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Tamaño del Feto */}
              <div className="space-y-2">
                <Label>Tamaño del Feto</Label>
                <Select 
                  value={formData.fetusSize} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, fetusSize: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Pequeño para la edad gestacional</SelectItem>
                    <SelectItem value="normal">Normal / Adecuado</SelectItem>
                    <SelectItem value="large">Grande para la edad gestacional</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Posición */}
              <div className="space-y-2">
                <Label>Posición del Feto</Label>
                <Select 
                  value={formData.position} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, position: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal / Correcta</SelectItem>
                    <SelectItem value="abnormal">Anormal / Requiere atención</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Veterinario */}
          <div className="space-y-2">
            <Label>Veterinario</Label>
            <Input
              placeholder="Nombre del veterinario..."
              value={formData.veterinarian}
              onChange={(e) => setFormData(prev => ({ ...prev, veterinarian: e.target.value }))}
            />
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label>Observaciones (Opcional)</Label>
            <Textarea
              placeholder="Observaciones adicionales sobre la palpación..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} className="bg-rose-600 hover:bg-rose-700">
            Guardar Palpación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
